import { useEffect } from "react";
import { useIsSmartEdit } from "~/app/config/pageMetadataContext";

/**
 * Full-page-load navigation under SmartEdit (ADR 0006, task 0159 Phase 4).
 *
 * SmartEdit historically expects full document loads so it can re-inject its
 * editorial map and re-scan slot/component wrappers. When `inSmartedit` is true,
 * internal same-origin link clicks must NOT be handled by TanStack Router — they
 * fall through to native full document loads.
 *
 * Mechanism: a single capture-phase `click` listener on `document`. The capture
 * phase runs top-down before the click reaches TanStack `Link`'s bubble-phase
 * `onClick` (which is delegated through React's root container), so we can
 * `preventDefault` + `stopPropagation` and force `window.location.assign(href)`.
 * This beats trying to opt out of `Link` per-route or to coax `Link` into not
 * calling `preventDefault`: it is one shell-root interception, works for plain
 * `<a>` tags and TanStack `Link` alike, and is a clean no-op when the SmartEdit
 * flag is off (the listener is never attached).
 */

function isModifiedClick(event: MouseEvent): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

function shouldForceFullPageLoad(anchor: HTMLAnchorElement, currentOrigin: string, currentHref: string): boolean {
  const hrefAttribute = anchor.getAttribute("href");
  if (!hrefAttribute) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(anchor.href);
  } catch {
    return false;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return false;
  }

  if (url.origin !== currentOrigin) {
    return false;
  }

  if (anchor.target === "_blank") {
    return false;
  }

  if (anchor.hasAttribute("download")) {
    return false;
  }

  if (url.href === currentHref && url.hash) {
    return false;
  }

  if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
    return false;
  }

  return true;
}

function preservePreviewTicket(destinationHref: string, currentSearch: string): string {
  const destination = new URL(destinationHref);
  const previewTicket = new URLSearchParams(currentSearch).get("cmsTicketId");

  if (previewTicket && !destination.searchParams.has("cmsTicketId")) {
    destination.searchParams.set("cmsTicketId", previewTicket);
  }

  return destination.href;
}

export function useSmartEditFullPageNavigation(): void {
  const inSmartEdit = useIsSmartEdit();

  useEffect(() => {
    if (!inSmartEdit) {
      return;
    }

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || isModifiedClick(event)) {
        return;
      }

      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) {
        return;
      }

      const anchor = eventTarget.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) {
        return;
      }

      if (!shouldForceFullPageLoad(anchor, window.location.origin, window.location.href)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      window.location.assign(preservePreviewTicket(anchor.href, window.location.search));
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
    };
  }, [inSmartEdit]);
}

/**
 * Mount-once side-effect component. Renders nothing; only attaches the
 * SmartEdit-gated full-page-load listener while mounted inside the shell root.
 */
export function SmartEditFullPageNavigation() {
  useSmartEditFullPageNavigation();
  return null;
}
